// Briefing-Formate fuer Claude, 1:1 aus der bisherigen ScannerPage (v5.3) uebernommen.
// Geaendert: Kopfzeile "Nexus" statt "AZSCAN", Export der Funktionen, Score in der Kopfzeile (19.10.2026).
/* eslint-disable @typescript-eslint/no-unused-vars */
// v5.1: Briefing-Helfer — Marktkontext, Confluence-Level, Earnings
export function fmtMarketContext(mc: any): string {
  if (!mc) return "";
  const p: string[] = [];
  // Markt (Index)
  if (mc.index)                     p.push(`Index ${mc.index}`);
  if (mc.trend)                     p.push(`Markt-Trend ${mc.trend}`);
  if (mc.regime)                    p.push(`Markt-Regime ${mc.regime}` +
                                            (mc.regime_adx != null ? ` (ADX ${mc.regime_adx})` : ""));
  if (mc.rsi != null)               p.push(`Markt-RSI ${mc.rsi}`);
  if (mc.perf_1d != null || mc.perf_5d != null || mc.perf_20d != null)
    p.push(`Index-Perf 1d/5d/20d ${mc.perf_1d ?? "?"}%/${mc.perf_5d ?? "?"}%/${mc.perf_20d ?? "?"}%`);
  // Symbol
  if (mc.symbol_regime)             p.push(`Symbol-Regime ${mc.symbol_regime}`);
  if (mc.symbol_adx != null)        p.push(`Symbol-ADX ${mc.symbol_adx}`);
  if (mc.rs_ratio != null)          p.push(`RS ${mc.rs_ratio}`);
  if (mc.confluence != null)        p.push(`Confluence ${mc.confluence}`);
  if (mc.above_ema50 != null || mc.above_ema200 != null)
    p.push(`über EMA50 ${mc.above_ema50 ? "ja" : "nein"} / EMA200 ${mc.above_ema200 ? "ja" : "nein"}`);
  // Weekly Bias
  if (mc.weekly_trend != null)      p.push(`Weekly-Trend ${mc.weekly_trend}`);
  if (mc.weekly_rsi != null)        p.push(`W-RSI ${mc.weekly_rsi}`);
  if (mc.weekly_ema20_slope != null) p.push(`W-Slope ${mc.weekly_ema20_slope}`);
  if (mc.close_over_open_20d != null) p.push(`C>O 20d ${mc.close_over_open_20d}%`);
  if (mc.active_clusters) {
    const ac = Array.isArray(mc.active_clusters)
      ? mc.active_clusters.join("/") : mc.active_clusters;
    if (ac) p.push(`Aktive Cluster ${ac}`);
  }
  return p.join(" · ");
}

export function fmtLevelLines(tl: any, full: boolean): string[] {
  if (!tl) return [];
  const out = [
    `Jetzt: Kurs ${tl.price_ref} · SL ${tl.sl} (${tl.sl_pct}%) · TP ${tl.tp} (${tl.tp_pct}%) · RRR ${tl.rrr ?? "—"} [${tl.entry_quality}]`,
  ];
  if (!full) return out;
  out.push(`  ATR ${tl.atr} (${tl.atr_pct}%) · Methode ${tl.method}` +
           (tl.bars_cleaned ? ` · ${tl.bars_cleaned} Bars bereinigt` : ""));
  if (tl.sl_cluster) {
    const c = tl.sl_cluster;
    out.push(`  SL-Cluster ${c.low}–${c.high} (Stärke ${c.strength}, ${c.n_sources} Quellen): ` +
             Array.from(new Set(c.labels || [])).join(", "));
  }
  if (tl.tp_cluster) {
    const c = tl.tp_cluster;
    out.push(`  TP-Cluster ${c.low}–${c.high} (Stärke ${c.strength}, ${c.n_sources} Quellen): ` +
             Array.from(new Set(c.labels || [])).join(", "));
  }
  if (tl.vol_to_tp_pct != null)
    out.push(`  Volumen zwischen Kurs und TP: ${tl.vol_to_tp_pct}% (60d-Profil)`);
  if (tl.warnings?.length)
    out.push(`  Hinweise: ${tl.warnings.join(" | ")}`);
  return out;
}

export function fmtEarnings(sd: any): string {
  const e = sd?._earnings;
  if (!e) return "";
  const d = e.days_until != null ? `in ${e.days_until}d` : "";
  return `Earnings ${d}${e.earnings_date ? ` (${e.earnings_date})` : ""}` +
         `${e.vega_window === "sweet_spot" ? " — Vega-Fenster" : ""}`;
}

// ── v5.3 (05.10.2026): Kompakt-Briefings (Full + Einzel, nur Swing/LT) ──
export function nf(x: any, d = 2): string {
  if (x === null || x === undefined || x === "" || isNaN(Number(x))) return "–";
  return Number(x).toLocaleString("de-DE", { maximumFractionDigits: d, useGrouping: false });
}
function pf(x: any): string {
  if (x === null || x === undefined || x === "" || isNaN(Number(x))) return "–";
  const a = Math.abs(Number(x));
  return nf(x, a >= 10 ? 2 : a >= 1 ? 3 : 4);
}
function ddmm(s: any): string {
  const m = String(s || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}.${m[2]}.` : "";
}
function berlinTs(ts: any): string {
  if (!ts) return "–";
  const d = new Date(ts);
  if (isNaN(d.getTime())) return String(ts);
  const o = { timeZone: "Europe/Berlin" };
  const day = d.toLocaleDateString("de-DE", { ...o, day: "2-digit", month: "2-digit" });
  const today = new Date().toLocaleDateString("de-DE", { ...o, day: "2-digit", month: "2-digit" });
  const t = d.toLocaleTimeString("de-DE", { ...o, hour: "2-digit", minute: "2-digit" });
  return day === today ? t : `${day} ${t} (alt)`;
}
function dataStand(tl: any): string {
  if (!tl) return "–";
  if (tl.price_source === "daily_close") return `Tagesschluss ${ddmm(tl.price_ts)}`.trim();
  if (!tl.price_ts) return tl.price_source ? String(tl.price_source) : "–";
  return berlinTs(tl.price_ts);
}
function liveNote(tl: any): string {
  if (tl && tl.adx_live != null) return `live ${berlinTs(tl.live_bar_ts)}`;
  if (!tl) return "live –";
  return tl.price_source === "daily_close" ? "live –: keine Intraday-Daten" : "live –: Tagesbar final";
}
export function sdFactor(sd: any, key: string): any {
  for (const [k, v] of Object.entries(sd || {})) {
    if (k.startsWith("_") || k === "confluence") continue;
    const f = (v as any)?.factors;
    if (f && f[key] !== undefined && f[key] !== null) return f[key];
  }
  return undefined;
}
function withLive(base: any, live: any, d = 1): string {
  return live != null ? `${nf(base, d)} (live ${nf(live, d)})` : nf(base, d);
}
function trendParts(s: any, withRsi: boolean): string[] {
  const mc = s.market_context || {}, tl = s.trade_levels || {};
  let adx = withLive(mc.symbol_adx, tl.adx_live);
  if (tl.adx_live != null && tl.adx_ref != null && mc.symbol_adx != null
      && Math.abs(Number(tl.adx_ref) - Number(mc.symbol_adx)) > 0.5) adx += ` [ref ${nf(tl.adx_ref, 1)}]`;
  const p = [`ADX ${adx}`, `DI ${withLive(mc.sym_di_diff, tl.di_diff_live)}`];
  if (withRsi) p.push(`RSI ${withLive(mc.sym_rsi, tl.rsi_live)}`);
  return p;
}
function setupParts(s: any): string[] {
  const mc = s.market_context || {}, sd = s.scoring_details || {};
  const e50 = mc.sym_ema50_ok == null ? "–" : (mc.sym_ema50_ok ? "ja" : "nein");
  const e200 = sdFactor(sd, "EMA200_Dist_Pct");
  return [`EMA200 ${e200 == null ? "–" : nf(e200, 1) + " %"}`, `EMA50 ${e50}`, `Conf ${mc.confluence ?? "–"}`,
          `W-RSI ${nf(mc.weekly_rsi, 1)}`, `Vol ${nf(mc.sym_vol_ratio ?? sdFactor(sd, "Vol_Ratio"))}`];
}
function marketStr(mc: any): string {
  const m = mc || {};
  return `Markt ${m.index || "–"}${m.regime ? " " + m.regime : ""} ADX ${nf(m.regime_adx, 0)} · RSI ${nf(m.rsi, 1)}`;
}
function earnStr(sd: any): string {
  const e = sd?._earnings;
  if (!e || (e.days_until == null && !e.earnings_date)) return "Earnings –";
  return `Earnings ${ddmm(e.earnings_date)}${e.days_until != null ? ` (${e.days_until}d)` : ""}`;
}
function hvStr(sd: any): string {
  const v = sd?._iv?.hv_percentile;
  return v == null ? "" : `HV-Pct ${v}`;
}
function stratExtras(sd: any): string {
  const p: string[] = [];
  const kl = sdFactor(sd, "Key_Level");
  if (kl != null) p.push(`Key-Level ${pf(kl)}${sdFactor(sd, "Confirmed") ? " (bestätigt)" : ""}`);
  const pat = sdFactor(sd, "Patterns");
  if (pat && pat !== "-" && !(Array.isArray(pat) && !pat.length))
    p.push(`Patterns ${Array.isArray(pat) ? pat.join("/") : pat}`);
  const rsiPb = sdFactor(sd, "RSI");
  if (rsiPb != null) p.push(`RSI-Pullback ${nf(rsiPb, 1)}`);
  const vw = sdFactor(sd, "vega_window");
  if (vw) p.push(`Vega ${vw}`);
  return p.join(" · ");
}
function clStr(c: any): string {
  if (!c) return "–";
  const lab = c.labels && c.labels.length ? " " + Array.from(new Set(c.labels)).join(",") : "";
  return `${pf(c.low)}–${pf(c.high)} (${nf(c.strength, 1)}/${c.n_sources ?? "?"})${lab}`;
}
export function fmtBriefingCompact(s: any): string {
  const sd = s.scoring_details || {}, mc = s.market_context || {}, tl = s.trade_levels;
  const dir = s.direction === "long" ? "▲ Long" : "▼ Short";
  const out = [`${s.symbol} ${s.company_name || ""} | ${s.exchange} | ${dir} | Score ${nf(s.score, 0)} | ${s.signal_type} | ${s.strategy}`];
  out.push(tl
    ? `Jetzt ${pf(tl.price_ref)} · SL ${pf(tl.sl)} · TP ${pf(tl.tp)} · RRR ${nf(tl.rrr)} [${tl.entry_quality}] · ATR ${pf(tl.atr)} · TP: ${tl.method} (Cluster TP ${nf(tl.tp_cluster?.strength, 1)} / SL ${nf(tl.sl_cluster?.strength, 1)})`
    : "Jetzt – (keine Level berechnet)");
  out.push(trendParts(s, false).concat(setupParts(s)).join(" · "));
  out.push([marketStr(mc), earnStr(sd), hvStr(sd), `Daten ${dataStand(tl)}`, liveNote(tl)].filter(Boolean).join(" · "));
  const ex = stratExtras(sd);
  if (ex) out.push(ex);
  return out.join("\n");
}
export function fmtBriefingDetail(s: any): string {
  const sd = s.scoring_details || {}, mc = s.market_context || {}, tl = s.trade_levels;
  const dir = s.direction === "long" ? "▲ Long" : "▼ Short";
  const out = [`📊 ${s.symbol} ${s.company_name || ""} | ${s.exchange}${s.currency ? " " + s.currency : ""} | ${dir} | Score ${nf(s.score, 0)} | ${s.signal_type} | ${s.strategy} | Gescannt ${s.scanned_at || "–"}`];
  if (tl) {
    out.push(`Jetzt ${pf(tl.price_ref)} · SL ${pf(tl.sl)} (${nf(tl.sl_pct)} %) · TP ${pf(tl.tp)} (${nf(tl.tp_pct)} %) · RRR ${nf(tl.rrr)} [${tl.entry_quality}] · Daten ${dataStand(tl)}`);
    out.push(`ATR ${pf(tl.atr)} (${nf(tl.atr_pct)} %) · ${tl.method} · Vol→TP ${tl.vol_to_tp_pct == null ? "–" : nf(tl.vol_to_tp_pct, 1) + " %"}`);
    out.push(`SL-Cl ${clStr(tl.sl_cluster)} · TP-Cl ${clStr(tl.tp_cluster)}`);
    if (tl.warnings && tl.warnings.length) out.push(`⚠ ${tl.warnings.join(" | ")}`);
  } else {
    out.push("Jetzt – (keine Level berechnet)");
  }
  out.push(trendParts(s, true).concat([liveNote(tl)]).join(" · "));
  out.push(setupParts(s).concat([earnStr(sd), hvStr(sd)]).filter(Boolean).join(" · "));
  out.push(marketStr(mc));
  const ex = stratExtras(sd);
  if (ex) out.push(ex);
  return out.join("\n");
}


// Volles Briefing fuer die aktuell gefilterte Liste ("Fuer Claude").
// Swing/Longterm: Kompaktformat. Intraday (scoring_details.criteria): Langformat wie bisher.
export function fmtBriefingList(signals: any[]): string {
  const lines = signals.map(signal => {
    if (!(signal.scoring_details as any)?.criteria) return "\n" + fmtBriefingCompact(signal);
    const sd = signal.scoring_details || {};
    const metrics = Object.entries(sd)
      .filter(([k]) => !["criteria","_earnings","strategy_info","_iv","_overextension"].includes(k))
      .map(([k, v]) => `  ${k}: ${v}`)
      .join("\n");
    const crits = sd.criteria
      ? Object.entries(sd.criteria)
          .map(([k, c]: [string, any]) => `  ${(c as any)?.hit ? "✅" : "❌"} ${k} (${(c as any)?.weight})`)
          .join("\n")
      : "";
    const clusters = `[Metriken]\n${metrics}\n\n[Kriterien]\n${crits}`;
    const mcStr = fmtMarketContext(signal.market_context);
    const lvl = fmtLevelLines(signal.trade_levels, false);
    const earn = fmtEarnings(sd);
    return [
      `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`,
      `📊 ${signal.symbol} (${signal.company_name}) | ${signal.signal_type} | ${signal.direction === "long" ? "▲ Long" : "▼ Short"} | Score: ${signal.score} | ${signal.exchange}`,
      `Strategie: ${signal.strategy}`,
      `Entry: ${signal.entry} | SL: ${signal.sl} | TP: ${signal.tp} | RR: 1:${signal.rr} (konstruktionsbedingt konstant)`,
      signal.entry_trigger ? `TV-Alert: ${signal.entry_trigger} (${signal.entry_trigger_type?.replace(/_/g," ")})` : "",
      ...lvl,
      mcStr ? `Kontext: ${mcStr}` : "",
      earn ? earn : "",
      ``,
      clusters,
    ].filter(Boolean).join("\n");
  });
  return [
    `📡 Nexus Briefing – ${new Date().toLocaleString("de-DE")}`,
    `${signals.length} Signale`,
    ``,
    ...lines
  ].join("\n");
}
